import i18next from 'i18next';
import * as ImagePicker from 'expo-image-picker';
import {
  ImagePickerAsset,
  ImagePickerOptions,
  ImagePickerResult,
} from 'expo-image-picker';
import { ImageManipulator, SaveFormat } from 'expo-image-manipulator';

import { IS_WEB, openAppSettings } from '@utils';
import AlertService from './AlertService';

export type ImagePickerResultType = ImagePickerAsset | null;

export interface IImagePickerOpenOptions
  extends ImagePickerOptions,
    Omit<IResizeImageParams, 'source'> {}

interface IResizeImageParams {
  source: ImagePickerAsset;
  width?: number;
  height?: number;
  format?: SaveFormat;
}

interface IRequestPermissionsParams {
  useCamera?: boolean;
}

class ImagePickerService {
  // TODO: Try to use permission service
  async requestPermissions(props?: IRequestPermissionsParams) {
    if (IS_WEB) {
      return true;
    }

    let status;

    if (props?.useCamera) {
      status = await ImagePicker.requestCameraPermissionsAsync();
    } else {
      status =
        await ImagePicker.requestMediaLibraryPermissionsAsync();
    }

    if (!status.granted) {
      const tKey = props?.useCamera
        ? 'permission.camera'
        : 'permission.photoLibrary';

      AlertService.permissionDenied(i18next.t(tKey), openAppSettings);

      return false;
    }

    return true;
  }

  private async _processResult(
    result: ImagePickerResult,
    options?: IImagePickerOpenOptions,
  ): Promise<ImagePickerResultType> {
    if (result.canceled) {
      return null;
    }

    let resultType = result.assets?.[0].type;

    if (!resultType) {
      resultType = result.assets?.[0].uri.match(/data:video\/*/gim)
        ? 'video'
        : 'image';
    }

    const res = { ...result.assets?.[0], type: resultType };

    if (
      resultType === 'image' &&
      (options?.width || options?.height)
    ) {
      return this.resizeImage({
        source: res,
        width: options.width,
        height: options.height,
        format: options?.format,
      });
    }

    return res;
  }

  async openPicker(
    options?: IImagePickerOpenOptions,
  ): Promise<ImagePickerResultType> {
    if (!(await this.requestPermissions())) {
      return null;
    }

    const result = await ImagePicker.launchImageLibraryAsync(options);

    return this._processResult(result, options);
  }

  async openCamera(
    options?: IImagePickerOpenOptions,
  ): Promise<ImagePickerResultType> {
    if (!(await this.requestPermissions({ useCamera: true }))) {
      return null;
    }

    const result = await ImagePicker.launchCameraAsync(options);

    return this._processResult(result, options);
  }

  // Sending original photos to the server is inefficient because the file size may too large (server burst).
  // Therefore, the resizing process is necessary
  async resizeImage({
    source,
    width,
    height,
    format = SaveFormat.JPEG,
  }: IResizeImageParams): Promise<ImagePickerAsset> {
    // Offsetting the X,Y axis to crop the center of the image
    let crop;
    if (source.height >= source.width) {
      crop = {
        originX: 0,
        originY: source.height / 2 - source.width / 2,
        width: source.width,
        height: source.width,
      };
    } else {
      crop = {
        originX: source.width / 2 - source.height / 2,
        originY: 0,
        width: source.height,
        height: source.height,
      };
    }

    // Resizing image to provided size
    const resize = {
      width,
      height,
    };

    const image = await ImageManipulator.manipulate(source.uri)
      .crop(crop)
      .resize(resize)
      .renderAsync();

    const result = await image.saveAsync({ format });

    return {
      ...source,
      ...result,
    };
  }
}

export default new ImagePickerService();
